import React, { useContext } from 'react'
import Select, { mergeStyles } from 'react-select'
import { Control, Controller } from 'react-hook-form'
import HelpIcon from '../ui/icons/HelpIcon'
import { useModal } from '../useModal'
import { ThemeContext } from '../../context/ThemeProvider'

interface Props {
  type?: string
  placeholder?: string
  name: string
  label?: string
  defaultValue?: string
  error?: any
  control?: Control<any>
  required?: boolean | string
  options: Array<any>
  infoMessage?: JSX.Element | string
  isClearable?: boolean
  disabled?: boolean
  isSearchable?: boolean
  customStyles?: any
  helpMessage?: string | React.ReactNode
  vanilla?: boolean
  value?: {
    label: string
    value: string | number | boolean
  }
  onChange?: (e: any) => void
}

const SelectInput = (props: Props) => {
  const {
    name,
    label,
    control,
    required,
    options,
    isClearable,
    placeholder,
    disabled,
    isSearchable,
    infoMessage,
    customStyles,
    helpMessage,
    vanilla = false,
    value,
    onChange,
  } = props
  const { handleModal } = useModal()
  const { theme } = useContext(ThemeContext)
  const isDark = theme === 'dark'

  if (vanilla) {
    return (
      <div className={`flex flex-1 flex-col gap-2`}>
        {label && (
          <div className="flex flex-row gap-2 items-center">
            <label className={`block  text-sm font-bold font-Karla`} htmlFor={name}>
              {label}
            </label>
            {helpMessage &&
              (typeof helpMessage === 'string' ? (
                <HelpIcon tooltip={helpMessage} />
              ) : (
                <button
                  onClick={(e) => {
                    e.preventDefault()
                    handleModal(helpMessage, { maxWidth: 600 })
                  }}
                  className=" underline text-xs">
                  What's this?
                </button>
              ))}
          </div>
        )}
        <Select
          value={value}
          id={name}
          classNamePrefix={'react-select'}
          isSearchable={isSearchable}
          isClearable={isClearable}
          components={{
            IndicatorSeparator: () => null,
          }}
          onChange={onChange}
          menuPlacement="auto"
          isDisabled={disabled}
          placeholder={placeholder}
          options={options}
          styles={styles(null, isDark)}
        />

        {infoMessage && (
          <span className={'font-medium text-xs text-foreground/60'}>{infoMessage}</span>
        )}
      </div>
    )
  }

  return (
    <Controller
      name={name}
      control={control}
      rules={{ required: disabled ? false : required ? 'This field is required' : false }}
      render={({
        field: { onChange, onBlur, value, name, ref },
        fieldState: { error },
      }) => (
        <div className={`flex flex-1 flex-col gap-2`}>
          {label && (
            <div className="flex flex-row gap-2 items-center">
              <label
                className={`block  ${required && 'required'} ${
                  error ? 'text-primary' : 'text-detailPrimary'
                } text-sm font-bold font-Karla`}
                htmlFor={name}>
                {label}
              </label>
              {helpMessage &&
                (typeof helpMessage === 'string' ? (
                  <HelpIcon tooltip={helpMessage} />
                ) : (
                  <button
                    onClick={(e) => {
                      e.preventDefault()
                      handleModal(helpMessage, { maxWidth: 600 })
                    }}
                    className=" underline text-xs">
                    What's this?
                  </button>
                ))}
            </div>
          )}
          <Select
            value={value}
            id={name}
            classNamePrefix={'react-select'}
            isSearchable={isSearchable}
            isClearable={isClearable}
            components={{
              IndicatorSeparator: () => null,
            }}
            menuPlacement="auto"
            isDisabled={disabled}
            placeholder={placeholder}
            ref={ref}
            onChange={onChange}
            options={options}
            styles={mergeStyles(styles(error, isDark), customStyles)}
          />

          {error && !disabled && (
            <span className={'font-medium text-xs text-[#F00]'}>{error.message}</span>
          )}
          {infoMessage && (
            <span className={'font-medium text-xs text-foreground/60'}>
              {infoMessage}
            </span>
          )}
        </div>
      )}
    />
  )
}

const styles = (error: any = undefined, isDark?: boolean) => ({
  control: (base: any) => ({
    ...base,
    border: error ? '2px solid #F00' : 'none',
    minWidth: 185,
    height: 32,
    minHeight: 32,
    fontWeight: 'bold',
    fontSize: 12,
    boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
    backgroundColor: isDark ? 'oklch(0.554 0.046 257.417)' : '#FFF',
  }),
  valueContainer: (base: any) => ({
    ...base,
    height: 32,
  }),
  indicatorsContainer: (base: any) => ({
    ...base,
    height: 32,
  }),
  menu: (base: any) => ({
    ...base,
    width: 'max-content',
    minWidth: '100%',
    fontWeight: 'bold',
    fontSize: 12,
    zIndex: 9999,
    backgroundColor: isDark ? 'oklch(0.446 0.043 257.281)' : '#FFF',
    color: isDark ? '#FFF' : '#09121F',
  }),
  dropdownIndicator: (base: any) => ({
    ...base,
    color: isDark ? '#000' : '#09121F', // Custom colour
    marginTop: 0,
  }),
  clearIndicator: (base: any) => ({
    ...base,
    marginTop: 4,
    color: isDark ? '#000' : '#09121F',
  }),
  singleValue: (base: any) => ({
    ...base,
    color: isDark ? '#FFF' : '#09121F', // Customize text color based on theme
  }),
  placeholder: (base: any) => ({
    ...base,
    color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(9,18,31,0.5)', // Slightly transparent for readability
  }),
  option: (base: any, state: any) => ({
    ...base,
    backgroundColor: state.isSelected
      ? isDark
        ? 'oklch(0.554 0.046 257.417)'
        : '#E0E0E0'
      : state.isFocused
      ? isDark
        ? 'oklch(0.5 0.05 257.4)'
        : '#F0F0F0'
      : 'transparent',
    color: isDark ? '#FFF' : '#09121F',
    ':active': {
      backgroundColor: isDark ? 'oklch(0.6 0.05 257.4)' : '#D0D0D0',
    },
  }),
})

export default SelectInput
